import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Heart, Instagram, Menu, MessageCircle, Search, ShoppingBag, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, imgUrl } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { useScrollVelocity } from "../hooks/useScrollVelocity";
import { Spin3D } from "./Spin3D";
import { GiftBox } from "./GiftBox";

function Ticker() {
  const { settings } = useStore();
  const text = settings?.announcement_text;
  if (!text) return null;
  const items = Array(6).fill(text);
  return (
    <div data-testid="announcement-ticker" className="overflow-hidden bg-foreground py-2 text-secondary">
      <div className="animate-marquee flex w-max whitespace-nowrap">
        {[0, 1].map((half) => (
          <div key={half} className="flex">
            {items.map((t, i) => (
              <span key={i} className="mx-6 text-[11px] font-light tracking-[0.25em] uppercase">{t}</span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(() => {
      api.get("/search/suggest", { params: { q } }).then((r) => setResults(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="search-overlay" className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <motion.div className="glass-deep mx-auto mt-24 w-[92%] max-w-xl rounded-3xl p-6 shadow-2xl"
            initial={{ y: -24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -24, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 border-b border-input pb-3">
              <Search size={18} className="text-primary" />
              <input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) { onClose(); navigate(`/shop?q=${encodeURIComponent(q.trim())}`); } }}
                placeholder="Search vintage watches, earrings, gifts..."
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
              <button onClick={onClose} data-testid="search-close" aria-label="Close search" className="rounded-full p-1 hover:bg-secondary"><X size={16} /></button>
            </div>
            {results.length > 0 && (
              <ul className="mt-3 max-h-72 overflow-auto" data-testid="search-suggestions">
                {results.map((p) => (
                  <li key={p.id}>
                    <button data-testid={`search-result-${p.id}`}
                      onClick={() => { onClose(); navigate(`/product/${p.id}`); }}
                      className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-secondary/60">
                      <span className="h-10 w-10 overflow-hidden rounded-lg bg-secondary">
                        {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}
                      </span>
                      <span className="text-sm">{p.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Header() {
  const { settings, cartCount, wishlist, categories } = useStore();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const mains = categories.filter((c) => c.is_main);
  const others = categories.filter((c) => !c.is_main && !c.parent_id);
  const navLink = ({ isActive }) =>
    `text-xs tracking-[0.2em] uppercase transition-colors ${isActive ? "text-primary" : "text-foreground/70 hover:text-foreground"}`;

  return (
    <>
      <header className="glass sticky top-0 z-40" data-testid="site-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Link to="/" data-testid="header-logo" className="flex min-w-0 items-center gap-2.5 sm:gap-3">
            {settings?.logo_url ? (
              <img src={imgUrl(settings.logo_url)} alt="Anaya logo" width="44" height="44" loading="eager" fetchPriority="high" decoding="async"
                onError={(e) => { const img = e.currentTarget; if (!img.dataset.retried) { img.dataset.retried = "1"; const s = img.src; img.src = ""; img.src = s + (s.includes("?") ? "&" : "?") + "r=1"; } }}
                className="h-10 w-10 shrink-0 rounded-full bg-secondary object-cover ring-1 ring-primary/30 sm:h-11 sm:w-11" />
            ) : (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary font-display text-xl text-primary sm:h-11 sm:w-11">A</span>
            )}
            <span className="flex min-w-0 flex-col justify-center" data-testid="header-brand-name">
              <span className="gold-text animate-shimmer whitespace-nowrap font-display text-[10px] font-semibold leading-tight tracking-[0.1em] sm:text-lg sm:tracking-[0.2em]">ANAYA FASHION</span>
              <span className="mt-0.5 text-[7px] leading-tight tracking-[0.38em] text-muted-foreground uppercase sm:text-[9px]">&amp; GIFTS</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
            <NavLink to="/" className={navLink} data-testid="nav-home">Home</NavLink>
            <NavLink to="/shop" className={navLink} data-testid="nav-shop">Shop</NavLink>
            <NavLink to="/category/vintage-watches" className={navLink} data-testid="nav-watches">Watches</NavLink>
            <NavLink to="/category/jewellery" className={navLink} data-testid="nav-jewellery">Jewellery</NavLink>
            <NavLink to="/portfolio" className={navLink} data-testid="nav-portfolio">Portfolio</NavLink>
            <NavLink to="/about" className={navLink} data-testid="nav-about">Our Story</NavLink>
            <NavLink to="/contact" className={navLink} data-testid="nav-contact">Contact</NavLink>
          </nav>
          <div className="flex shrink-0 items-center gap-1.5">
            <button data-testid="header-search-btn" onClick={() => setSearchOpen(true)} aria-label="Search"
              className="rounded-full p-2.5 transition-colors hover:bg-secondary"><Search size={18} /></button>
            <Link to="/wishlist" data-testid="header-wishlist-btn" aria-label="Wishlist" className="relative rounded-full p-2.5 transition-colors hover:bg-secondary">
              <Heart size={18} />
              {wishlist.length > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[9px] font-semibold text-accent-foreground">{wishlist.length}</span>}
            </Link>
            <Link to="/cart" data-testid="header-cart-btn" aria-label="Shopping bag" className="relative rounded-full p-2.5 transition-colors hover:bg-secondary">
              <ShoppingBag size={18} />
              {cartCount > 0 && <span data-testid="cart-count-badge" className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-semibold text-white">{cartCount}</span>}
            </Link>
            <button data-testid="mobile-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Menu" className="rounded-full p-2.5 hover:bg-secondary lg:hidden">
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <AnimatePresence>
        {menuOpen && (
          <motion.div data-testid="mobile-menu" className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm lg:hidden"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMenuOpen(false)}>
            <motion.div className="glass-deep ml-auto h-full w-80 max-w-[85%] overflow-auto p-6"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <span className="font-display text-lg tracking-[0.25em]">MENU</span>
                <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="rounded-full p-2 hover:bg-secondary"><X size={18} /></button>
              </div>
              <nav className="mt-6 flex flex-col gap-1">
                {[{ to: "/", label: "Home" }, { to: "/shop", label: "Shop All" }, { to: "/portfolio", label: "Portfolio" }, { to: "/about", label: "Our Story" }, { to: "/contact", label: "Contact" }, { to: "/track", label: "Track Order" }].map((l) => (
                  <NavLink key={l.to} to={l.to} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="rounded-xl px-3 py-2.5 text-sm tracking-widest uppercase hover:bg-secondary">{l.label}</NavLink>
                ))}
                <p className="mt-4 px-3 text-[10px] tracking-[0.3em] text-muted-foreground uppercase">Categories</p>
                {mains.map((c) => (
                  <div key={c.id}>
                    <NavLink to={`/category/${c.slug}`} onClick={() => setMenuOpen(false)} data-testid={`mobile-cat-${c.slug}`}
                      className="block rounded-xl px-3 py-2 text-sm hover:bg-secondary">{c.name}</NavLink>
                    {categories.filter((s) => s.parent_id === c.id).map((s) => (
                      <NavLink key={s.id} to={`/category/${s.slug}`} onClick={() => setMenuOpen(false)} data-testid={`mobile-cat-${s.slug}`}
                        className="block rounded-xl py-1.5 pl-7 pr-3 text-xs text-muted-foreground hover:bg-secondary">{s.name}</NavLink>
                    ))}
                  </div>
                ))}
                {others.length > 0 && <p className="mt-3 px-3 text-[10px] tracking-[0.3em] text-muted-foreground uppercase">More</p>}
                {others.map((c) => (
                  <NavLink key={c.id} to={`/category/${c.slug}`} onClick={() => setMenuOpen(false)} data-testid={`mobile-cat-${c.slug}`}
                    className="block rounded-xl px-3 py-2 text-sm text-muted-foreground hover:bg-secondary">{c.name}</NavLink>
                ))}
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Footer() {
  const { settings, categories } = useStore();
  return (
    <footer className="mt-24 border-t border-white/60 bg-white/50 backdrop-blur" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            {settings?.logo_url && <img src={imgUrl(settings.logo_url)} alt="Anaya logo" className="h-14 w-14 rounded-full object-cover ring-1 ring-primary/30" />}
            <div>
              <p className="gold-text font-display text-xl font-semibold tracking-[0.18em]">ANAYA FASHION</p>
              <p className="text-[9px] tracking-[0.42em] text-muted-foreground uppercase">&amp; Gifts — more than just a store</p>
            </div>
          </div>
          <p className="mt-4 text-sm font-light leading-relaxed text-muted-foreground">{settings?.footer_note || "Thoughtful picks for every you."}</p>
          {settings?.address && <p className="mt-2 text-xs font-light text-muted-foreground">{settings.address}</p>}
        </div>
        <div>
          <p className="text-xs font-medium tracking-[0.25em] uppercase">Shop</p>
          <ul className="mt-4 space-y-2 text-sm font-light">
            <li><Link to="/shop" className="hover:text-primary">All Products</Link></li>
            {categories.filter((c) => c.is_main).slice(0, 6).map((c) => (
              <li key={c.id}><Link to={`/category/${c.slug}`} className="hover:text-primary">{c.name}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-medium tracking-[0.25em] uppercase">Help</p>
          <ul className="mt-4 space-y-2 text-sm font-light">
            <li><Link to="/track" className="hover:text-primary">Track Your Order</Link></li>
            <li><Link to="/policies/shipping" className="hover:text-primary">Shipping Policy</Link></li>
            <li><Link to="/policies/returns" className="hover:text-primary">Returns &amp; Exchange</Link></li>
            <li><Link to="/policies/privacy" className="hover:text-primary">Privacy Policy</Link></li>
            <li><Link to="/policies/terms" className="hover:text-primary">Terms of Service</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-medium tracking-[0.25em] uppercase">Say Hello</p>
          <ul className="mt-4 space-y-2 text-sm font-light">
            <li><a href={`https://wa.me/${settings?.whatsapp || "919643444088"}`} target="_blank" rel="noreferrer" className="hover:text-primary">WhatsApp us</a></li>
            <li><a href={settings?.instagram || "https://www.instagram.com/anaya.shop__"} target="_blank" rel="noreferrer" className="hover:text-primary">Instagram</a></li>
            <li className="text-muted-foreground">{settings?.email}</li>
            <li className="text-muted-foreground">{settings?.address}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/60 py-5 text-center text-[11px] tracking-[0.25em] text-muted-foreground uppercase">
        Anaya Fashion &amp; Gifts — by Afia &amp; Anaya · Curated with love
      </div>
    </footer>
  );
}

function FloatingSocial() {
  const { settings } = useStore();
  const velocity = useScrollVelocity();
  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col gap-3">
      <div className="animate-float" style={{ animationDelay: "0s" }}>
        <motion.a data-testid="floating-whatsapp" href={`https://wa.me/${settings?.whatsapp || "919643444088"}?text=${encodeURIComponent("Hi Anaya! I have a question about an order.")}`}
          target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp"
          className="flex items-center justify-center rounded-full bg-[#25D366] p-3.5 text-white shadow-xl"
          whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.94 }}>
          <Spin3D velocity={velocity} axis="x" baseSpeed={0.008} boost={1.1}>
            <MessageCircle size={22} />
          </Spin3D>
        </motion.a>
      </div>
      <div className="animate-float" style={{ animationDelay: "1.2s" }}>
        <motion.a data-testid="floating-instagram" href={settings?.instagram || "https://www.instagram.com/anaya.shop__"}
          target="_blank" rel="noreferrer" aria-label="Follow on Instagram"
          className="flex items-center justify-center rounded-full bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] p-3.5 text-white shadow-xl"
          whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.94 }}>
          <Spin3D velocity={velocity} axis="x" baseSpeed={0.008} boost={1.1}>
            <Instagram size={22} />
          </Spin3D>
        </motion.a>
      </div>
    </div>
  );
}

function Decor() {
  const velocity = useScrollVelocity();
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      <div className="animate-float absolute left-[45%] top-[8%] font-display text-4xl text-accent-foreground/20" style={{ animationDelay: "4.2s" }}>&#10084;</div>
      <div className="animate-float absolute bottom-[30%] right-[30%] font-display text-5xl text-primary/15" style={{ animationDelay: "3.4s" }}>&#10022;</div>
      <div className="animate-float absolute right-[4%] top-[14%] hidden md:block" style={{ animationDelay: "0.4s" }} data-testid="giftbox-large">
        <Spin3D velocity={velocity} baseSpeed={0.012} boost={0.5} tilt={16}>
          <GiftBox size={132} opacity={0.9} />
        </Spin3D>
      </div>
      <div className="animate-float absolute left-[3%] top-[42%]" style={{ animationDelay: "1.6s" }} data-testid="giftbox-small-1">
        <Spin3D velocity={velocity} baseSpeed={0.02} boost={0.7} tilt={20}>
          <GiftBox size={64} opacity={0.75} />
        </Spin3D>
      </div>
      <div className="animate-float absolute bottom-[16%] right-[16%]" style={{ animationDelay: "2.8s" }} data-testid="giftbox-small-2">
        <Spin3D velocity={velocity} baseSpeed={0.016} boost={0.6} tilt={18}>
          <GiftBox size={80} opacity={0.8} />
        </Spin3D>
      </div>
      <div className="animate-float absolute bottom-[8%] left-[12%] blur-[1px]" style={{ animationDelay: "3.6s" }} data-testid="giftbox-small-3">
        <Spin3D velocity={velocity} baseSpeed={0.024} boost={0.8} tilt={24}>
          <GiftBox size={48} opacity={0.6} />
        </Spin3D>
      </div>
      <div className="animate-float absolute left-[46%] top-[64%] hidden blur-[2px] md:block" style={{ animationDelay: "4.4s" }} data-testid="giftbox-small-4">
        <Spin3D velocity={velocity} baseSpeed={0.028} boost={0.9} tilt={22}>
          <GiftBox size={40} opacity={0.5} />
        </Spin3D>
      </div>
    </div>
  );
}

export default function Layout() {
  return (
    <div className="bg-boutique relative min-h-screen">
      <Decor />
      <div className="relative z-10">
        <Ticker />
        <Header />
        <main>
          <Outlet />
        </main>
        <Footer />
        <FloatingSocial />
      </div>
    </div>
  );
}
